// Coordinates are normalized to the source page as displayed by PDF.js,
// including its intrinsic rotation, but before rotations made in the editor.
export const rotateAnnotationRect = (rect, rotation) => {
  const { x, y, width, height } = rect;
  switch ((rotation % 360 + 360) % 360) {
    case 90: return { ...rect, x: 1 - y - height, y: x, width: height, height: width };
    case 180: return { ...rect, x: 1 - x - width, y: 1 - y - height };
    case 270: return { ...rect, x: y, y: 1 - x - width, width: height, height: width };
    default: return { ...rect };
  }
};

export const pageDisplaySize = (page) => {
  const box = page.getCropBox();
  const sideways = Math.abs(page.getRotation().angle % 180) === 90;
  return { width: sideways ? box.height : box.width, height: sideways ? box.width : box.height };
};

export const annotationPdfPlacement = (page, rect) => {
  const box = page.getCropBox();
  const rotation = (page.getRotation().angle % 360 + 360) % 360;
  const size = pageDisplaySize(page);
  const u = rect.x;
  const v = rect.y + rect.height;
  let x; let y;
  switch (rotation) {
    case 90: x = v * box.width; y = u * box.height; break;
    case 180: x = (1 - u) * box.width; y = v * box.height; break;
    case 270: x = (1 - v) * box.width; y = (1 - u) * box.height; break;
    default: x = u * box.width; y = (1 - v) * box.height;
  }
  return { x: box.x + x, y: box.y + y, width: rect.width * size.width, height: rect.height * size.height, rotation };
};

export const drawPdfAnnotations = async (document, page, annotations = []) => {
  const { degrees, rgb } = await import("pdf-lib");
  // Original text masks stay in place even when replacement text is moved.
  for (const annotation of annotations) {
    if (!annotation.originalRect) continue;
    const { rotation, ...placement } = annotationPdfPlacement(page, annotation.originalRect);
    page.drawRectangle({ ...placement, rotate: degrees(rotation), color: rgb(1, 1, 1) });
  }
  for (const annotation of annotations) {
    if (!annotation.image) continue;
    const image = await document.embedPng(annotation.image);
    const { rotation, ...placement } = annotationPdfPlacement(page, annotation);
    page.drawImage(image, { ...placement, rotate: degrees(rotation) });
  }
};

export const rasterizeText = async ({ text, fontSize, color, bold, background, textAngle = 0 }, maxWidth) => {
  if (document.fonts?.ready) await document.fonts.ready;
  const size = Math.max(6, Math.min(144, Number(fontSize) || 18));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const font = `${bold ? "bold " : ""}${size}px sans-serif`;
  context.font = font;
  const lines = [];
  const widthLimit = Math.max(size * 2, maxWidth);
  String(text).split("\n").forEach((paragraph) => {
    let line = "";
    for (const character of Array.from(paragraph)) {
      if (line && context.measureText(line + character).width > widthLimit) {
        lines.push(line); line = "";
      }
      line += character;
    }
    lines.push(line);
  });
  const padding = 2;
  const width = Math.max(size, ...lines.map((line) => context.measureText(line).width)) + padding * 2;
  const height = Math.max(1, lines.length) * size * 1.3 + padding * 2;
  // Keep large pasted text bounded rather than allocating an unlimited canvas.
  if (height > 8000 || lines.length > 250) throw new Error("文字太長，請分成數個文字框。");
  const scale = Math.min(3, 4096 / Math.max(width, height));
  canvas.width = Math.ceil(width * scale); canvas.height = Math.ceil(height * scale);
  context.scale(scale, scale);
  if (background) { context.fillStyle = "#ffffff"; context.fillRect(0, 0, width, height); }
  context.font = font; context.fillStyle = color; context.textBaseline = "top";
  lines.forEach((line, index) => context.fillText(line, padding, padding + index * size * 1.3));
  if (!textAngle) return { image: canvas.toDataURL("image/png"), width, height };
  const radians = textAngle * Math.PI / 180;
  const rotated = document.createElement("canvas");
  const rotatedWidth = Math.abs(width * Math.cos(radians)) + Math.abs(height * Math.sin(radians));
  const rotatedHeight = Math.abs(width * Math.sin(radians)) + Math.abs(height * Math.cos(radians));
  rotated.width = Math.ceil(rotatedWidth * scale); rotated.height = Math.ceil(rotatedHeight * scale);
  const target = rotated.getContext("2d");
  target.translate(rotated.width / 2, rotated.height / 2); target.rotate(radians);
  target.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return { image: rotated.toDataURL("image/png"), width: rotatedWidth, height: rotatedHeight };
};


// Crop is normalized to the original PDF.js viewport, before editor rotation.
export const applyPdfCrop = (page, crop) => {
  if (!crop) return;
  const box = page.getCropBox();
  const rect = rotateAnnotationRect(crop, -page.getRotation().angle);
  page.setCropBox(box.x + rect.x * box.width,
    box.y + (1 - rect.y - rect.height) * box.height,
    rect.width * box.width, rect.height * box.height);
};
